import React, { useState } from 'react';
import { Mail, ArrowLeft, Send } from 'lucide-react';
import { supabase } from '../supabase/client';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import TruckLogo from './TruckLogo';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!email.trim()) {
      setError(t('errors.emailRequired'));
      return;
    }
    
    setLoading(true);
    
    try {
      // Usar el método de restablecimiento de Supabase
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
      
      setSuccessMessage(t('auth.resetPasswordSuccess'));
      
      // Esperar un momento antes de redirigir al login
      setTimeout(() => {
        onBackToLogin();
      }, 3000);
    } catch (err: any) {
      console.error('Error sending password reset email:', err);
      setError(err.message || t('errors.resetPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: 'url("https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")',
          backgroundSize: 'cover'
        }}
      >
        {/* Overlay to darken the image and improve text readability */}
        <div className="absolute inset-0 bg-black opacity-60"></div>
      </div>

      <div className="w-full max-w-md z-10 px-4">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="relative h-40 sm:h-48 bg-gradient-to-r from-primary-light to-primary-dark">
            <div className="absolute inset-0 bg-black opacity-30"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <TruckLogo size={64} className="text-white" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
              <h1 className="text-2xl sm:text-3xl font-bold uppercase">
                COTIZATRIN
              </h1>
              <p className="text-xs sm:text-sm opacity-80">{t('pdf.title')}</p>
            </div>
            <div className="absolute top-0 right-0 p-3">
              <LanguageSwitcher variant="icon" className="text-white hover:bg-white/20" />
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center mb-6">
              <button
                onClick={onBackToLogin}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <h2 className="text-2xl font-semibold text-gray-800">{t('auth.resetPassword')}</h2>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
                {successMessage}
              </div>
            )}
            
            <p className="mb-6 text-gray-600 text-sm">
              {t('auth.resetPasswordInstructions')}
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail size={16} className="text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="ejemplo@correo.com"
                    disabled={loading}
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-md transition duration-200 flex items-center justify-center"
              >
                {loading ? (
                  <span className="animate-pulse">{t('common.loading')}</span>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    {t('auth.sendResetLink')}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
        
        <div className="mt-4 text-center text-white text-sm">
          © 2025 Cotizatrin. Todos los derechos reservados.
          <div className="mt-2 text-xs text-gray-300">
            <p>+52 (55) 1234-5678 | info@cotizatrin.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;